import React, { useRef, useState } from "react";
import { View, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import { useToolTip } from "@/hooks/useToolTip";
import useContextMenu from "@/hooks/useContextMenu";
import Animated, { useAnimatedStyle, withTiming, useSharedValue } from "react-native-reanimated";

export interface ClockInOption {
  key: string;
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  title: string;
  description: string;
  tooltip: string;
}

export interface ClockInOptionsProps {
  options?: ClockInOption[];
  values: Record<string, boolean>;
  editing?: boolean;
  onChange: (values: Record<string, boolean>) => void;
  label?: string;
  info?: string;
  /** Optional extra content rendered at the end of the label row */
  trailing?: React.ReactNode;
}

const DEFAULT_OPTIONS: ClockInOption[] = [
  {
    key: "app",
    icon: "cellphone",
    title: "Mobile App",
    description: "GPS verified",
    tooltip:
      "Workers clock in/out using the mobile app with GPS location verification to confirm they're at the job site.",
  },
  {
    key: "qr_code",
    icon: "qrcode-scan",
    title: "QR Code",
    description: "Scan at site",
    tooltip: "Workers scan a unique QR code posted at the job site to clock in/out. Requires physical presence.",
  },
  {
    key: "timesheet",
    icon: "clipboard-text-clock",
    title: "Supervisor Report",
    description: "End of shift",
    tooltip:
      "Supervisor records attendance at the end of the shift. May include paper sign-in sheets or verbal confirmation. Workers do not clock in/out themselves.",
  },
];

// Separate component to handle hover state for each menu option
function MenuOption({
  option,
  isEnabled,
  colors,
  toolTip,
  onSelect,
}: {
  option: ClockInOption;
  isEnabled: boolean;
  colors: any;
  toolTip: any;
  onSelect: () => void;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const showTooltip = (e: any) => {
    const target = e.target as any;
    target?.measureInWindow?.((tx: number, ty: number, tw: number) => {
      toolTip.setToolTipContent(
        <View
          style={{
            backgroundColor: colors.background,
            borderColor: colors.border,
            borderWidth: 1,
            borderRadius: 8,
            padding: 10,
            maxWidth: 260,
          }}
        >
          <ThemedText style={{ fontSize: 12, lineHeight: 18 }}>{option.tooltip}</ThemedText>
        </View>,
      );
      toolTip.setLocation({ x: tx + tw / 2 + 10, y: ty - 70 } as any);
      toolTip.setVisible(true);
    });
  };

  return (
    <Pressable
      onPress={onSelect}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={({ pressed }) => [
        styles.menuOption,
        {
          backgroundColor: isEnabled ? colors.primary + "15" : isHovered ? colors.backgroundSecondary : "transparent",
        },
        pressed && { opacity: 0.7 },
      ]}
    >
      <View
        style={[
          styles.iconContainer,
          {
            backgroundColor: isEnabled ? colors.primary + "20" : colors.border + "40",
          },
        ]}
      >
        <MaterialCommunityIcons
          name={option.icon}
          size={16}
          color={isEnabled ? colors.primary : colors.textSecondary}
        />
      </View>
      <View style={styles.optionContent}>
        <View style={styles.optionTitleRow}>
          <ThemedText style={[styles.optionTitle, isEnabled && { color: colors.primary }]}>{option.title}</ThemedText>
          <Pressable onHoverIn={showTooltip} onHoverOut={() => toolTip.setVisible(false)} hitSlop={8}>
            <MaterialCommunityIcons name="information-outline" size={14} color={colors.textSecondary} />
          </Pressable>
        </View>
        <ThemedText style={[styles.optionDescription, { color: colors.textSecondary }]}>
          {option.description}
        </ThemedText>
      </View>
      <MaterialCommunityIcons
        name={isEnabled ? "checkbox-marked" : "checkbox-blank-outline"}
        size={22}
        color={isEnabled ? colors.primary : colors.textSecondary}
      />
    </Pressable>
  );
}

export function ClockInOptions({
  options = DEFAULT_OPTIONS,
  values,
  editing = false,
  onChange,
  label,
  info,
  trailing,
}: ClockInOptionsProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const contextMenu = useContextMenu();
  const selectorRef = useRef<View>(null);
  const rotation = useSharedValue(0);

  const handleSelect = (key: string) => {
    const newValues: Record<string, boolean> = {};
    options.forEach((opt) => {
      newValues[opt.key] = opt.key === key;
    });
    onChange(newValues);
  };

  const selectedOptions = options.filter((opt) => values[opt.key]);

  const handleToggle = () => {
    if (!editing) return;

    contextMenu.setVisible(true);
    rotation.value = withTiming(180, { duration: 150 });
    contextMenu.setIgnoreRef(selectorRef);

    selectorRef.current?.measureInWindow((x, y, w, h) => {
      contextMenu.setTriggerElement?.({ x, y, width: w ?? 0, height: h ?? 0 });
      contextMenu.setLocation({ x, y: y + (h ?? 0) + 4 });
      contextMenu.setMenuContent(
        <View style={{ minWidth: w, maxWidth: 400 }}>
          {options.map((option) => {
            const isEnabled = values[option.key] ?? false;
            return (
              <MenuOption
                key={option.key}
                option={option}
                isEnabled={isEnabled}
                colors={colors}
                toolTip={toolTip}
                onSelect={() => {
                  handleSelect(option.key);
                  contextMenu.setVisible(false);
                  rotation.value = withTiming(0, { duration: 150 });
                }}
              />
            );
          })}
        </View>,
      );
      contextMenu.setStyle({ backgroundColor: colors.card, overflow: "hidden", borderRadius: 10 });
    });
  };

  const animatedIconStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  return (
    <View style={styles.container}>
      <View style={styles.labelRow}>
        {label && <ThemedText style={[styles.label, { color: colors.textSecondary }]}>{label}</ThemedText>}
        {info && (
          <View style={[styles.infoTag, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
            <MaterialCommunityIcons name="information-outline" size={11} color={colors.textSecondary} />
            <ThemedText style={[styles.infoText, { color: colors.textSecondary }]}>{info}</ThemedText>
          </View>
        )}
        {trailing && <View style={{ marginLeft: "auto" }}>{trailing}</View>}
      </View>
      <Pressable
        ref={selectorRef}
        style={[
          styles.selector,
          {
            backgroundColor: colors.backgroundSecondary,
            borderColor: colors.border,
          },
        ]}
        onPress={handleToggle}
      >
        <View style={styles.selectedContent}>
          {selectedOptions.length > 0 ? (
            <View style={styles.selectedRow}>
              <MaterialCommunityIcons name={selectedOptions[0].icon} size={18} color={colors.primary} />
              <ThemedText style={styles.selectedText}>{selectedOptions[0].title}</ThemedText>
              <Pressable
                onPress={(e) => e.stopPropagation()}
                onHoverIn={(e) => {
                  e.stopPropagation();
                  const target = e.target as any;
                  target?.measureInWindow?.((tx: number, ty: number, tw: number) => {
                    toolTip.setToolTipContent(
                      <View
                        style={{
                          backgroundColor: colors.background,
                          borderColor: colors.border,
                          borderWidth: 1,
                          borderRadius: 8,
                          padding: 10,
                          maxWidth: 260,
                        }}
                      >
                        <ThemedText style={{ fontSize: 12, lineHeight: 18 }}>{selectedOptions[0].tooltip}</ThemedText>
                      </View>,
                    );
                    toolTip.setLocation({ x: tx + tw / 2 + 10, y: ty - 70 } as any);
                    toolTip.setVisible(true);
                  });
                }}
                onHoverOut={() => toolTip.setVisible(false)}
                hitSlop={8}
              >
                <MaterialCommunityIcons name="information-outline" size={14} color={colors.textSecondary} />
              </Pressable>
            </View>
          ) : (
            <ThemedText style={[styles.placeholder, { color: colors.placeholder }]}>
              Select clock-in method...
            </ThemedText>
          )}
        </View>
        {editing && (
          <Animated.View style={animatedIconStyle}>
            <MaterialCommunityIcons
              name="chevron-down"
              size={16}
              color={colors.textSecondary}
              style={{ opacity: 0.5 }}
            />
          </Animated.View>
        )}
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  label: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  infoTag: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: StyleSheet.hairlineWidth,
  },
  infoText: {
    fontSize: 10,
  },
  selector: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 40,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  selectedContent: {
    flex: 1,
  },
  selectedRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  selectedText: {
    fontSize: 13,
    fontWeight: "600",
  },
  placeholder: {
    fontSize: 13,
  },
  menuOption: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 10,
    gap: 8,
  },
  iconContainer: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  optionContent: {
    flex: 1,
  },
  optionTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  optionTitle: {
    fontSize: 13,
    fontWeight: "600",
  },
  optionDescription: {
    fontSize: 11,
  },
});
